import React from 'react';
import {
  DatePickerModal,
  formatDatePickerDate,
} from 'QuoteApp/components/DatePickerModal';
import {
  AccidentChallengeFieldsType,
  AccidentChallengeFormValuesType,
} from 'QuoteApp/services/endpoints/PostChallengeAccident';
import { generateFieldDataTestId } from 'QuoteApp/utils/formik';
import { Column, FormInput } from 'Shared/components';
import { getMonthNameFromNumber } from 'Shared/utils/date';
import { FormikErrors } from 'formik';

import styles from './styles.module.css';
import { useAppContext } from 'QuoteApp/services/providers';
import { AccidentDataType } from 'QuoteApp/services/endpoints/GetDetails';
import { getDisplayValue } from '../ReportDetailRow';
import { capitalizeFirstLetter } from 'Policies/utils/helpers';
import { formatMoney } from 'Shared/utils';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import { Checkbox } from '@clearcover/atlas';
import classNames from 'classnames';

type AccidentChallengeFormPropsType = {
  accident: AccidentDataType;
  identifier: string;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  values: AccidentChallengeFormValuesType;
  errors: FormikErrors<AccidentChallengeFormValuesType>;
  isEditing: boolean;
  availableDrivers?: Record<string, string>;
};

export const getInputProps = (
  fieldName: keyof AccidentChallengeFieldsType,
  values: AccidentChallengeFormPropsType['values'],
  identifier: string,
  setFieldValue: AccidentChallengeFormPropsType['setFieldValue'],
  errors: AccidentChallengeFormPropsType['errors']
) => {
  const fullFieldName = `${identifier}.${fieldName}`;
  return {
    name: fullFieldName,
    className: styles.formInput,
    'data-testid': generateFieldDataTestId(fieldName),
    variant: 'selectAutocomplete' as const,
    onChange: (value: string) => {
      setFieldValue(fullFieldName, value);
    },
    value: values[identifier][fieldName],
    errorMessage: errors[identifier]?.[fieldName],
  };
};

export const AccidentChallengeForm = ({
  accident,
  identifier,
  values,
  setFieldValue,
  errors,
  isEditing,
  availableDrivers,
}: AccidentChallengeFormPropsType) => {
  const { config } = useAppContext();
  const {
    assumeAtFaultFlag,
    collectTypeOfLossFlag,
  } = useQuoteAppFeatureFlags();

  const renderChallengeAccidentForm = () => (
    <>
      <Column variant="quarter" className={styles.datePickerWrapper}>
        <DatePickerModal
          modalHeader="Commission Effective Date"
          formInputClassName={styles.formInput}
          name="effectiveDate"
          startDate={
            values[identifier].date
              ? new Date(values[identifier].date ?? '')
              : undefined
          }
          onDateChange={date => {
            setFieldValue(`${identifier}.day`, date.getDate());
            setFieldValue(
              `${identifier}.month`,
              getMonthNameFromNumber(date.getMonth() + 1).toLocaleLowerCase()
            );
            setFieldValue(`${identifier}.year`, date.getFullYear());
            setFieldValue(`${identifier}.date`, formatDatePickerDate(date));
          }}
        />
      </Column>
      {availableDrivers && (
        <Column variant="quarter">
          <FormInput
            {...getInputProps(
              'operatorId',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            data-testid="accident-operator-field"
            variant="selectAutocomplete"
            options={availableDrivers}
          />
        </Column>
      )}
      {collectTypeOfLossFlag && (
        <>
          <Column variant="sixth">
            <FormInput
              {...getInputProps(
                'accidentType',
                values,
                identifier,
                setFieldValue,
                errors
              )}
              data-testid="accident-type-field"
              variant="selectAutocomplete"
              options={
                values[identifier].operatorId === 'unlisted_driver' ||
                (!values[identifier].operatorId && accident.isLendingLoss)
                  ? config?.drivers.accidents.nonListedDriverClaimTypes || {}
                  : config?.drivers.accidents.driverClaimTypes || {}
              }
            />
          </Column>
          <Column variant="sixth" className={styles.lossAmountWrapper}>
            <FormInput
              {...getInputProps(
                'lossAmount',
                values,
                identifier,
                setFieldValue,
                errors
              )}
              data-testid="accident-type-field"
              variant="text"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(
                  `${identifier}.lossAmount`,
                  event.currentTarget.value
                );
              }}
            />
          </Column>
        </>
      )}
      {!assumeAtFaultFlag && (
        <Column variant={'quarter'} className={styles.atFaultCheckboxWrapper}>
          <Checkbox
            {...getInputProps(
              'atFault',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            value={/true/i.test(values[identifier]['atFault'])}
            label="This was an at-fault accident"
            data-testid="accident-at-fault-field"
            onChange={(value: boolean) =>
              setFieldValue(`${identifier}.atFault`, value.toString())
            }
            className={styles.atFaultCheckbox}
          />
        </Column>
      )}
    </>
  );

  const renderReadOnly = () => (
    <div
      data-testid={`accidents-challenge-read-only-attributes-${accident.identifier}`}
      className={styles.accidentsChalengeReadOnlyAttributesWrapper}
    >
      <Column
        variant="quarter"
        className={classNames('fs-mask', styles.datePickerWrapper)}
        data-testid="accidents-challenge-date"
      >
        {accident.date.value}
      </Column>
      {availableDrivers && (
        <Column
          variant="quarter"
          className="fs-mask"
          data-testid="accidents-challenge-operator"
        >
          {availableDrivers[values[identifier].operatorId ?? ''] ||
            availableDrivers[accident.operatorId ?? ''] ||
            '-'}
        </Column>
      )}
      {collectTypeOfLossFlag && (
        <>
          <Column
            variant="sixth"
            className="fs-mask"
            data-testid="accidents-challenge-accident-type"
          >
            {getDisplayValue(
              capitalizeFirstLetter(accident.accidentType.value || '-')
            )}
          </Column>
          <Column
            variant="sixth"
            data-testid="accidents-challenge-loss-amount"
            className={classNames('fs-mask', styles.lossAmountWrapper)}
          >
            {accident.lossAmount.value
              ? formatMoney(accident.lossAmount.value)
              : '-'}
          </Column>
        </>
      )}
      {!assumeAtFaultFlag && (
        <Column variant="quarter" className="fs-mask">
          {getDisplayValue(values[identifier].atFault)}
        </Column>
      )}
    </div>
  );

  return <>{isEditing ? renderChallengeAccidentForm() : renderReadOnly()}</>;
};
